import type { JSX, ReactNode } from "react";

import { Toast as ArkToast, toastAnatomy } from "@ark-ui/react/toast";
import { IconAlertCircle } from "@mobsuccess-devops/icons/alert-circle";
import { IconCheckCircle1 } from "@mobsuccess-devops/icons/check-circle-1";
import { IconInformationCircle } from "@mobsuccess-devops/icons/information-circle";
import { IconRoadSignWarning } from "@mobsuccess-devops/icons/road-sign-warning";
import { sva, type RecipeVariant } from "@mobsuccess-devops/styled-system/css";
import type { Options } from "@zag-js/toast";

import { CircularProgress } from "../_PandaArk/Progress/CircularProgress";

type PaletteVariant = RecipeVariant<typeof styles>["palette"];
type FlashType = "success" | "danger" | "info" | "warning" | "loading";

const icons = {
  info: IconInformationCircle.Bold,
  danger: IconAlertCircle.Bold,
  success: IconCheckCircle1.Bold,
  warning: IconRoadSignWarning.Bold,
} satisfies Record<Exclude<FlashType, "loading">, React.FC>;

type FlashProps = Options<ReactNode>;

function isPalette(type: unknown): type is PaletteVariant {
  return (
    ["success", "danger", "warning", "info"] satisfies PaletteVariant[]
  ).some((palette) => palette === String(type));
}

function toFlashType(type?: unknown): FlashType {
  switch (type) {
    case "success":
    case "danger":
    case "info":
    case "warning":
    case "loading":
      return type;
    default:
      return "info";
  }
}

export function Flash({ title, meta }: FlashProps): JSX.Element {
  const type = toFlashType(meta?.type);
  const showIcon = meta?.icon !== false;
  const classes = styles({
    palette: isPalette(meta?.palette) ? meta.palette : undefined,
  });
  return (
    <ArkToast.Root className={classes.root}>
      {showIcon && <FlashIcon type={type} className={classes.icon} />}
      <ArkToast.Title className={classes.title}>{title}</ArkToast.Title>
    </ArkToast.Root>
  );
}

type FlashIconProps = {
  type: FlashType;
  className?: string;
};

function FlashIcon({ type, className }: FlashIconProps): JSX.Element {
  if (type === "loading") {
    return (
      <CircularProgress
        size="xs"
        value="indeterminate"
        palette="info"
        className={className}
      />
    );
  }
  const Icon = icons[type];
  return <Icon className={className} color={type} />;
}

const styles = sva({
  slots: toastAnatomy.extendWith("icon").keys(),
  base: {
    root: {
      display: "grid",
      gap: "xs",
      gridTemplateAreas: `"icon title"`,
      gridTemplateColumns: "auto 1fr",
      gridTemplateRows: "auto",
      alignItems: "center",
      opacity: "var(--opacity)",
      zIndex: "var(--z-index)",
      listStyle: "none",
      boxShadow:
        "0 4px 16px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.08)",
      height: "var(--height)",
      width: "max-content",
      maxWidth: "520px",
      scale: "var(--scale)",
      translate: "var(--x) var(--y) 0",
      willChange: "translate, opacity, scale",
      transitionDuration: "slow",
      transitionProperty: "translate, scale, opacity, height",
      transitionTimingFunction: "default",
      paddingBlock: "xs",
      paddingInline: "sm",
      borderRadius: "md",
      borderLeft: "2px solid token(colors.colorPalette)",
      backgroundColor: "white",
      animation: "slide-in .2s ease-in-out",
      _closed: {
        animation: "slide-out 0.2s ease-in-out",
      },
    },
    icon: {
      alignSelf: "center",
      gridArea: "icon",
      size: "xs",
    },
    title: {
      gridArea: "title",
      color: "colorPalette",
      textStyle: "body",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  variants: {
    palette: {
      success: { root: { colorPalette: "success" } },
      danger: { root: { colorPalette: "danger" } },
      info: { root: { colorPalette: "info" } },
      warning: { root: { colorPalette: "warning" } },
    },
  },
  defaultVariants: {
    palette: "info",
  },
});
